<template>
  <div class="container-fluid">
    <div class="row mb-3 align-items-center">
      <div class="col">
        <h4 class="mb-1">Peajes</h4>
        <p class="text-muted mb-0">
          Lista de peajes usados al estimar viajes en la app del cliente.
        </p>
      </div>
      <div class="col-auto">
        <button class="btn btn-primary" @click="openCreate">
          Nuevo peaje
        </button>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>
    <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>

    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-5 text-muted">Cargando peajes…</div>
        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tarifa (CLP)</th>
                <th>Ubicación</th>
                <th>Estado</th>
                <th class="text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="toll in tolls" :key="toll.id">
                <td>{{ toll.name }}</td>
                <td>${{ formatAmount(toll.amount) }}</td>
                <td>
                  <small class="text-muted">
                    {{ toll.latitude.toFixed(6) }}, {{ toll.longitude.toFixed(6) }}
                  </small>
                </td>
                <td>
                  <span :class="toll.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                    {{ toll.isActive ? 'Activo' : 'Inactivo' }}
                  </span>
                </td>
                <td class="text-right text-nowrap">
                  <button class="btn btn-sm btn-outline-info mr-1" title="Ver en mapa" @click="openMap(toll)">
                    Visualizar
                  </button>
                  <button class="btn btn-sm btn-outline-primary mr-1" @click="openEdit(toll)">
                    Editar
                  </button>
                  <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(toll)">
                    Eliminar
                  </button>
                </td>
              </tr>
              <tr v-if="!tolls.length">
                <td colspan="5" class="text-center text-muted py-4">
                  No hay peajes configurados.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal editar / crear -->
    <div v-if="showForm" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,.45)">
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="saveForm">
            <div class="modal-header">
              <h5 class="modal-title">{{ editingId ? 'Editar peaje' : 'Nuevo peaje' }}</h5>
              <button type="button" class="close" @click="showForm = false">
                <span>&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label>Nombre</label>
                <input v-model="form.name" class="form-control" required />
              </div>
              <div class="form-group">
                <label>Tarifa (CLP)</label>
                <input v-model.number="form.amount" type="number" min="0" step="1" class="form-control" required />
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Latitud</label>
                    <input v-model.number="form.latitude" type="number" step="any" class="form-control" required />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Longitud</label>
                    <input v-model.number="form.longitude" type="number" step="any" class="form-control" required />
                  </div>
                </div>
              </div>
              <div class="form-group form-check">
                <input id="tollActive" v-model="form.isActive" type="checkbox" class="form-check-input" />
                <label class="form-check-label" for="tollActive">Activo</label>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-light" @click="showForm = false">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                {{ saving ? 'Guardando…' : 'Guardar' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal mapa -->
    <div v-if="mapToll" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,.45)">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ mapToll.name }}</h5>
            <button type="button" class="close" @click="mapToll = null">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body p-0">
            <iframe
              :src="mapEmbedUrl"
              width="100%"
              height="420"
              style="border:0"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              title="Ubicación del peaje"
            />
            <div class="p-3">
              <div class="mb-1"><strong>Tarifa:</strong> ${{ formatAmount(mapToll.amount) }} CLP</div>
              <div class="text-muted small">
                {{ mapToll.latitude }}, {{ mapToll.longitude }}
              </div>
              <a :href="mapExternalUrl" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary mt-2">
                Abrir en OpenStreetMap
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { createToll, deleteToll, fetchTolls, updateToll } from '@/services/tollsApi';

export default {
  name: 'PeajesPage',
  data() {
    return {
      loading: true,
      saving: false,
      tolls: [],
      errorMessage: '',
      successMessage: '',
      showForm: false,
      editingId: null,
      mapToll: null,
      form: {
        name: '',
        amount: 0,
        latitude: 0,
        longitude: 0,
        isActive: true,
      },
    };
  },
  computed: {
    mapEmbedUrl() {
      if (!this.mapToll) return '';
      const lat = this.mapToll.latitude;
      const lng = this.mapToll.longitude;
      const delta = 0.02;
      return (
        `https://www.openstreetmap.org/export/embed.html?bbox=` +
        `${lng - delta}%2C${lat - delta}%2C${lng + delta}%2C${lat + delta}` +
        `&layer=mapnik&marker=${lat}%2C${lng}`
      );
    },
    mapExternalUrl() {
      if (!this.mapToll) return '#';
      return `https://www.openstreetmap.org/?mlat=${this.mapToll.latitude}&mlon=${this.mapToll.longitude}#map=15/${this.mapToll.latitude}/${this.mapToll.longitude}`;
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatAmount(value) {
      return Number(value || 0).toLocaleString('es-CL');
    },
    async load() {
      this.loading = true;
      this.errorMessage = '';
      try {
        this.tolls = await fetchTolls();
      } catch (e) {
        this.errorMessage = e.message || 'Error al cargar peajes';
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editingId = null;
      this.form = {
        name: '',
        amount: 900,
        latitude: -41.47,
        longitude: -72.94,
        isActive: true,
      };
      this.showForm = true;
    },
    openEdit(toll) {
      this.editingId = toll.id;
      this.form = {
        name: toll.name,
        amount: Number(toll.amount),
        latitude: Number(toll.latitude),
        longitude: Number(toll.longitude),
        isActive: !!toll.isActive,
      };
      this.showForm = true;
    },
    openMap(toll) {
      this.mapToll = toll;
    },
    async saveForm() {
      this.saving = true;
      this.errorMessage = '';
      this.successMessage = '';
      try {
        const body = {
          name: this.form.name.trim(),
          amount: Number(this.form.amount),
          latitude: Number(this.form.latitude),
          longitude: Number(this.form.longitude),
          isActive: !!this.form.isActive,
        };
        if (this.editingId) {
          await updateToll(this.editingId, body);
          this.successMessage = 'Peaje actualizado';
        } else {
          await createToll(body);
          this.successMessage = 'Peaje creado';
        }
        this.showForm = false;
        await this.load();
      } catch (e) {
        this.errorMessage = e.message || 'Error al guardar';
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(toll) {
      const result = await Swal.fire({
        title: '¿Eliminar peaje?',
        text: toll.name,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
      });
      if (!result.isConfirmed) return;
      try {
        await deleteToll(toll.id);
        this.successMessage = 'Peaje eliminado';
        await this.load();
      } catch (e) {
        this.errorMessage = e.message || 'Error al eliminar';
      }
    },
  },
};
</script>
